import { useEffect, useRef } from "react";
import "./TesseractSpin.css";

type Vec4 = [number, number, number, number];

const VERTICES: Vec4[] = [-1, 1].flatMap((x) => [-1, 1].flatMap((y) => [-1, 1].flatMap((z) => [-1, 1].map((w): Vec4 => [x, y, z, w]))));
const EDGES: [number, number][] = [];
for (let i = 0; i < VERTICES.length; i += 1) {
  for (let j = i + 1; j < VERTICES.length; j += 1) {
    if (VERTICES[i].filter((value, axis) => value !== VERTICES[j][axis]).length === 1) EDGES.push([i, j]);
  }
}

function rotateXW(x: number, y: number, z: number, w: number, angle: number): Vec4 {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return [x * c - w * s, y, z, x * s + w * c];
}

function rotateYW(x: number, y: number, z: number, w: number, angle: number): Vec4 {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return [x, y * c - w * s, z, y * s + w * c];
}

function rotateYZ(x: number, y: number, z: number, w: number, angle: number): Vec4 {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return [x, y * c - z * s, y * s + z * c, w];
}

function project(angle: number, size: number) {
  const distance = 3.2;
  return VERTICES.map((vertex) => {
    let [x, y, z, w] = vertex;
    [x, y, z, w] = rotateXW(x, y, z, w, angle);
    [x, y, z, w] = rotateYW(x, y, z, w, angle * 0.37);
    [x, y, z, w] = rotateYZ(x, y, z, w, 0.55);
    const scale = distance / (distance - w);
    const px = (x * 0.92 - z * 0.28) * scale;
    const py = (y * 0.92 + z * 0.22) * scale;
    const radius = size * 0.22;
    return [size / 2 + px * radius, size / 2 + py * radius, scale] as const;
  });
}

const FACES: number[][] = [];
for (let axisA = 0; axisA < 4; axisA += 1) {
  for (let axisB = axisA + 1; axisB < 4; axisB += 1) {
    for (const signA of [-1, 1]) {
      for (const signB of [-1, 1]) {
        FACES.push(VERTICES.map((vertex, index) => vertex[axisA] === signA && vertex[axisB] === signB ? index : -1).filter((index) => index >= 0));
      }
    }
  }
}

function faceOrder(indices: number[], points: readonly (readonly [number, number, number])[]) {
  const mid = { x: 0, y: 0 };
  for (const index of indices) { mid.x += points[index][0]; mid.y += points[index][1]; }
  return indices
    .map((index) => [Math.atan2(points[index][1] - mid.y / indices.length, points[index][0] - mid.x / indices.length), index] as const)
    .sort((a, b) => a[0] - b[0])
    .map((entry) => entry[1]);
}

function paint(ctx: CanvasRenderingContext2D, size: number, angle: number) {
  ctx.clearRect(0, 0, size, size);
  const radius = size * 0.22;
  ctx.fillStyle = "#5b2d9a";
  ctx.beginPath();
  ctx.moveTo(radius, 0);
  ctx.arcTo(size, 0, size, size, radius);
  ctx.arcTo(size, size, 0, size, radius);
  ctx.arcTo(0, size, 0, 0, radius);
  ctx.arcTo(0, 0, size, 0, radius);
  ctx.closePath();
  ctx.fill();
  const points = project(angle, size);
  const faces = FACES.map((indices) => ({
    indices: faceOrder(indices, points),
    depth: indices.reduce((sum, index) => sum + points[index][2], 0) / indices.length,
  })).sort((a, b) => a.depth - b.depth);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  for (const face of faces) {
    ctx.beginPath();
    face.indices.forEach((index, order) => {
      const [x, y] = points[index];
      if (order === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.fillStyle = `rgba(255, 213, 106, ${0.07 + (face.depth - 0.75) * 0.12})`;
    ctx.fill();
  }
  for (const [a, b] of EDGES) {
    const depth = (points[a][2] + points[b][2]) / 2;
    ctx.strokeStyle = depth > 1.02 ? "#ffe7a0" : "#e4b7ff";
    ctx.globalAlpha = 0.55 + Math.min(0.4, (depth - 0.7) * 0.6);
    ctx.lineWidth = size * (depth > 1.02 ? 0.07 : 0.045);
    ctx.beginPath();
    ctx.moveTo(points[a][0], points[a][1]);
    ctx.lineTo(points[b][0], points[b][1]);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  for (const [x, y, depth] of points) {
    ctx.fillStyle = depth > 1.02 ? "#fff6d4" : "#f0d4ff";
    ctx.beginPath();
    ctx.arc(x, y, size * (0.032 + depth * 0.01), 0, Math.PI * 2);
    ctx.fill();
  }
}

export function TesseractSpin({ size = 48, className = "" }: { size?: number; className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    let angle = 0.7;
    let visible = true;
    const draw = (timestamp: number) => {
      if (!media.matches && visible) angle = timestamp / 1400;
      paint(ctx, size, angle);
      if (!media.matches && visible) frame = window.requestAnimationFrame(draw);
    };
    paint(ctx, size, angle);
    if (!media.matches) frame = window.requestAnimationFrame(draw);
    const onMotion = () => {
      window.cancelAnimationFrame(frame);
      if (!media.matches && visible) frame = window.requestAnimationFrame(draw);
      else paint(ctx, size, 0.7);
    };
    media.addEventListener("change", onMotion);
    const io = new IntersectionObserver((entries) => {
      visible = entries.some((entry) => entry.isIntersecting);
      window.cancelAnimationFrame(frame);
      if (visible && !media.matches) frame = window.requestAnimationFrame(draw);
    });
    io.observe(canvas);
    return () => {
      window.cancelAnimationFrame(frame);
      media.removeEventListener("change", onMotion);
      io.disconnect();
    };
  }, [size]);

  return <canvas className={`tesseract-spin ${className}`.trim()} ref={canvasRef} width={size} height={size} aria-hidden="true" />;
}

export const TESSERACT_EDGE_COUNT = EDGES.length;
